import { useState, useEffect, useRef } from 'react';
import { Poll } from '@/types/poll';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryParam } from '@/hooks/use-query-param';

export const usePollNavigation = (polls: Poll[]) => {
  const [currentPollIndex, setCurrentPollIndex] = useState(0);
  
  // Use the query param hook for cleaner parameter management
  const [indexParam, setIndexParam, removeIndexParam] = useQueryParam('index');
  
  // Keep track of whether navigation is in progress to prevent redundant updates
  const navigationInProgress = useRef(false);
  const navigationTimeoutRef = useRef<number | null>(null);
  
  // Initialize index from URL on first load
  useEffect(() => {
    if (indexParam && !navigationInProgress.current) {
      const parsedIndex = parseInt(indexParam, 10);
      if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < polls.length) {
        setCurrentPollIndex(parsedIndex);
      }
    }
  }, [indexParam, polls.length]);

  // Update URL only when index changes from internal navigation
  useEffect(() => {
    // Only update URL if this change was triggered by a navigation action (not from URL)
    if (navigationInProgress.current) {
      // Debounce URL updates to prevent flickering
      if (navigationTimeoutRef.current) {
        window.clearTimeout(navigationTimeoutRef.current);
      }
      
      navigationTimeoutRef.current = window.setTimeout(() => {
        setIndexParam(currentPollIndex.toString());
        navigationTimeoutRef.current = null;
        
        // Reset the navigation flag after a short delay to allow for proper rendering
        setTimeout(() => {
          navigationInProgress.current = false;
        }, 50);
      }, 50);
    }
  }, [currentPollIndex, setIndexParam]);

  const nextPoll = () => {
    if (currentPollIndex < polls.length - 1) {
      navigationInProgress.current = true;
      setCurrentPollIndex(currentPollIndex + 1);
    }
  };

  const prevPoll = () => {
    if (currentPollIndex > 0) {
      navigationInProgress.current = true;
      setCurrentPollIndex(currentPollIndex - 1);
    }
  };
  
  const setCurrentIndex = (index: number) => {
    if (index >= 0 && index < polls.length && index !== currentPollIndex) {
      navigationInProgress.current = true;
      setCurrentPollIndex(index);
    }
  };
  
  // Clean up any pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        window.clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);
  
  return {
    currentPollIndex,
    setCurrentPollIndex: setCurrentIndex,
    nextPoll,
    prevPoll
  };
};
